/**
 * CustomSelect
 *
 * Projenin standart açılır liste görünümü. Sayfalarda tekrarlanan
 * `appearance-none` + ayrı `▼` span kalıbının tek bileşene alınmış hâli
 * (kaynak desen: pages/tanimlar/StokUrun.js).
 *
 * Uzun listelerde `aramali` verilir; o zaman portal üzerinden açılan,
 * arama kutulu bir liste çizilir (KasaModals.SearchableSelect davranışı).
 *
 * Props:
 *   value       – seçili değer
 *   onChange    – (deger) => void   ← event değil, doğrudan değer döner
 *   options     – [{ deger, etiket }] | string[]
 *   placeholder – boş seçenek metni (verilirse listeye "" değerli satır eklenir)
 *   disabled
 *   aramali     – true ise arama kutulu portal listesi
 *   className   – dış kapsayıcıya eklenir
 *   renk        – odak rengi: 'violet' | 'cyan' | 'indigo' | 'emerald' (varsayılan violet)
 *
 * Kullanım:
 *   <CustomSelect value={tip} onChange={setTip} placeholder="Tümü"
 *     options={tipler.map(t => ({ deger: t.hizmetTipiId, etiket: t.ad }))} />
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const ODAK = {
  violet:  'focus:border-violet-400 focus:ring-violet-100',
  cyan:    'focus:border-cyan-400 focus:ring-cyan-100',
  indigo:  'focus:border-indigo-400 focus:ring-indigo-100',
  emerald: 'focus:border-emerald-400 focus:ring-emerald-100',
};

const SECILI = {
  violet:  'bg-violet-600 text-white',
  cyan:    'bg-cyan-600 text-white',
  indigo:  'bg-indigo-600 text-white',
  emerald: 'bg-emerald-600 text-white',
};

// string[] de kabul edilir; normalize edip tek biçime çeviriyoruz
function normalize(options) {
  return (options || []).map(o =>
    (o && typeof o === 'object') ? { deger: o.deger, etiket: o.etiket ?? String(o.deger) }
                                 : { deger: o, etiket: String(o) }
  );
}

export default function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  aramali = false,
  className = '',
  renk = 'violet',
}) {
  const secenekler = normalize(options);
  const odak = ODAK[renk] || ODAK.violet;

  if (!aramali) {
    return (
      <div className={`relative ${className}`}>
        <select
          value={value ?? ''}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          className={`w-full appearance-none cursor-pointer bg-white border-2 border-gray-100 rounded-2xl
                      pl-5 pr-11 py-3.5 text-xs font-bold text-gray-800 leading-none outline-none
                      focus:ring-4 transition-all disabled:bg-gray-50 disabled:text-gray-400
                      disabled:cursor-not-allowed ${odak}`}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {secenekler.map(o => (
            <option key={o.deger} value={o.deger}>{o.etiket}</option>
          ))}
        </select>
        <span className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 font-black text-xs pointer-events-none">
          ▼
        </span>
      </div>
    );
  }

  return <AramaliSelect
    value={value} onChange={onChange} secenekler={secenekler}
    placeholder={placeholder} disabled={disabled} className={className}
    odak={odak} secili={SECILI[renk] || SECILI.violet} />;
}

function AramaliSelect({ value, onChange, secenekler, placeholder, disabled, className, odak, secili }) {
  const [acik, setAcik] = useState(false);
  const [ara, setAra] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, yukari: false });
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setAcik(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function ac() {
    if (disabled) return;
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      const altBosluk = window.innerHeight - r.bottom;
      setPos(altBosluk < 260
        ? { yukari: true,  bottom: window.innerHeight - r.top + window.scrollY, left: r.left + window.scrollX, width: r.width }
        : { yukari: false, top: r.bottom + window.scrollY + 4,                  left: r.left + window.scrollX, width: r.width });
    }
    setAcik(p => !p);
    setAra('');
  }

  const metin = ara.trim().toLocaleLowerCase('tr-TR');
  const liste = metin
    ? secenekler.filter(o => o.etiket.toLocaleLowerCase('tr-TR').includes(metin))
    : secenekler;
  const seciliEtiket = secenekler.find(o => String(o.deger) === String(value))?.etiket;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <div onClick={ac}
        className={`w-full bg-white border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold
                    leading-none flex items-center justify-between transition-all ${odak}
                    ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'cursor-pointer text-gray-800'}`}>
        <span className={seciliEtiket ? '' : 'text-gray-400'}>
          {seciliEtiket || placeholder || 'Seçiniz'}
        </span>
        <span className="text-gray-400 text-xs ml-2">{acik ? '▲' : '▼'}</span>
      </div>

      {acik && createPortal(
        <div
          style={{
            position: 'absolute',
            top: pos.yukari ? 'auto' : pos.top,
            bottom: pos.yukari ? pos.bottom : 'auto',
            left: pos.left, width: pos.width, zIndex: 11000,
          }}
          className="bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
          onMouseDown={e => e.stopPropagation()}>
          <input autoFocus value={ara} onChange={e => setAra(e.target.value)}
            className="w-full px-4 py-2.5 text-sm border-b border-gray-100 focus:outline-none"
            placeholder="Ara..." />
          <div className="max-h-56 overflow-y-auto">
            {placeholder !== undefined && !metin && (
              <div onClick={() => { onChange(''); setAcik(false); }}
                className="px-4 py-2.5 text-sm cursor-pointer text-gray-400 hover:bg-gray-50">
                {placeholder}
              </div>
            )}
            {liste.length === 0
              ? <div className="px-4 py-2.5 text-sm text-gray-400">Sonuç yok</div>
              : liste.map(o => (
                <div key={o.deger} onClick={() => { onChange(o.deger); setAcik(false); }}
                  className={'px-4 py-2.5 text-sm cursor-pointer font-semibold ' +
                    (String(o.deger) === String(value) ? secili : 'text-gray-700 hover:bg-gray-50')}>
                  {o.etiket}
                </div>
              ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
